import { ref, computed, ComputedRef } from 'vue'
import { useJourneyStore } from '@/stores/journeyStore'
import { Travel, SearchSelection } from '@/types'

export function useSearchResultSelection() {
  const journeyStore = useJourneyStore()
  const selectedTravel = ref<(Travel & Partial<SearchSelection>) | null>(null)
  const selectedReturnTravel = ref<(Travel & Partial<SearchSelection>) | null>(null)
  const showOutbound = ref(true)

  const updateSelectedTravel = (selection: SearchSelection) => {
    const travels = journeyStore.journeySearchResults?.data?.offer?.travels || []
    const selectedTravelData = travels.find(
      (travel) => travel.direction === 'outbound'
    )
    if (selectedTravelData) {
      const selectedTravelCopy = JSON.parse(JSON.stringify(selectedTravelData))
      
      const updatedRoutes = selectedTravelCopy.routes.map((route: any) => {
        return {
          ...route,
          legs: route.legs.map((leg: any, index: number) => {
            const updatedLeg = {
              ...leg,
              id: `${selectedTravelData.id}_${index}`,
            }
            
            if (updatedLeg.seat) {
              updatedLeg.seat = {
                ...updatedLeg.seat,
                status: 'AVAILABLE',
                seat_number: updatedLeg.seat.seat_number || '',
              }
            }
            
            return updatedLeg
          }),
        }
      })
      
      selectedTravel.value = {
        ...selectedTravelCopy,
        routes: updatedRoutes,
        ...selection,
      }
    }
  }

  const updateSelectedReturnTravel = (selection: SearchSelection) => {
    const travels = journeyStore.journeySearchResults?.data?.offer?.travels || []
    const selectedTravelData = travels.find(
      (travel) => travel.direction === 'inbound'
    )
    if (selectedTravelData) {
      const selectedTravelCopy = JSON.parse(JSON.stringify(selectedTravelData))
      
      const updatedRoutes = selectedTravelCopy.routes.map((route: any) => {
        return {
          ...route,
          legs: route.legs.map((leg: any, index: number) => {
            const updatedLeg = {
              ...leg,
              id: `${selectedTravelData.id}_${index}`,
            }
            
            if (updatedLeg.seat) {
              updatedLeg.seat = {
                ...updatedLeg.seat,
                status: 'AVAILABLE',
                seat_number: updatedLeg.seat.seat_number || '',
              }
            }
            
            return updatedLeg
          }),
        }
      })
      
      selectedReturnTravel.value = {
        ...selectedTravelCopy,
        routes: updatedRoutes,
        ...selection,
      }
    }
  }

  const updateShowOutbound = (value: boolean) => {
    showOutbound.value = value
  }

  const selectedTravelObject = computed(() => {
    if (!selectedTravel.value) return null
    return {
      ...selectedTravel.value,
      routeIndex: selectedTravel.value.routeIndex ?? 0,
      tariffCode: selectedTravel.value.tariffCode || 'STANDARD'
    }
  })

  const selectedReturnTravelObject = computed(() => {
    if (!selectedReturnTravel.value) return null
    return {
      ...selectedReturnTravel.value,
      routeIndex: selectedReturnTravel.value.routeIndex ?? 0,
      tariffCode: selectedReturnTravel.value.tariffCode || 'STANDARD'
    }
  })

  const returnTravels = computed(() => {
    const travels =
      (journeyStore.journeySearchResults?.data?.offer?.travels?.filter(
        (travel) => travel?.direction === 'inbound'
      ) as Travel[]) || []
    return travels
  })

  const hasResults = computed(() => {
    const outboundTravel =
      journeyStore.journeySearchResults?.data?.offer?.travels?.find(
        (travel) => travel.direction === 'outbound'
      )
    return Boolean(outboundTravel?.routes?.length)
  })

  const hasReturnResults = computed(() => {
    const returnTravel =
      journeyStore.journeySearchResults?.data?.offer?.travels?.find(
        (travel) => travel.direction === 'inbound'
      )
    return Boolean(returnTravel?.routes?.length)
  })

  const showInboundTravels = () => {
    showOutbound.value = false
  }

  return {
    selectedTravel,
    selectedReturnTravel,
    showOutbound,
    updateSelectedTravel,
    updateSelectedReturnTravel,
    updateShowOutbound,
    selectedTravelObject,
    selectedReturnTravelObject,
    returnTravels,
    hasResults,
    hasReturnResults,
    showInboundTravels
  }
}
